import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessSetupValidation } from '../hooks/useBusinessSetupValidation';
import axios from 'axios';

interface BusinessSetupModalProps {
  open: boolean;
  onComplete: () => void;
}

interface FormData {
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  tax_rate: number;
  late_fee_mode: 'flat' | 'percent' | 'off' | string;
  late_fee_amount: number;
}

export function BusinessSetupModal({ open, onComplete }: BusinessSetupModalProps) {
  const { currentBusiness, token, isCookieMode, API_BASE_URL, csrfToken } = useAuth();
  const { missingCriticalFields, missingWarningFields, businessSettings, refreshValidation } = useBusinessSetupValidation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    tax_rate: 8.375,
    late_fee_mode: 'flat',
    late_fee_amount: 25.00,
  });

  useEffect(() => {
    if (currentBusiness && businessSettings) {
      setFormData({
        name: currentBusiness.name || '',
        street: currentBusiness.street || '',
        city: currentBusiness.city || '',
        state: currentBusiness.state || '',
        zip: currentBusiness.zip || '',
        phone: currentBusiness.phone || '',
        email: currentBusiness.email || '',
        tax_rate: businessSettings.tax_rate || 8.375,
        late_fee_mode: businessSettings.late_fee_mode || 'flat',
        late_fee_amount: businessSettings.late_fee_amount || 25.00,
      });
    }
  }, [currentBusiness, businessSettings]);

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isLateFeeOff = (mode: string) => mode === 'off';
  
  const handleLateFeeModeChange = (mode: 'flat' | 'percent' | 'off' | string) => {
    setFormData(prev => ({ 
      ...prev, 
      late_fee_mode: mode,
      late_fee_amount: isLateFeeOff(mode) ? 0 : prev.late_fee_amount
    }));
  };

  const handleLateFeeAmountChange = (amount: number) => {
    setFormData(prev => ({ ...prev, late_fee_amount: amount }));
  };

  const getLateFeeAmountError = () => {
    if (isLateFeeOff(formData.late_fee_mode)) return null;
    if (formData.late_fee_amount < 0.01) return 'Amount must be at least $0.01';
    if (formData.late_fee_mode === 'percent' && formData.late_fee_amount > 100) {
      return 'Percentage cannot exceed 100%';
    }
    if (formData.late_fee_mode === 'flat' && formData.late_fee_amount > 9999.99) {
      return 'Flat fee cannot exceed $9,999.99';
    }
    return null;
  };

  const handleSubmit = async () => {
    if (!currentBusiness || !token) return;

    setLoading(true);
    setError(null);

    try {
      const headers: any = {};
      
      // Only add Authorization header if not in cookie mode
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      // Update business info
      await axios.put(`${API_BASE_URL}/businesses/${currentBusiness.id}`, {
        name: formData.name,
        street: formData.street,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        phone: formData.phone,
        email: formData.email,
      }, {
        headers,
        withCredentials: isCookieMode,
      });

      // Update business settings
      await axios.put(`${API_BASE_URL}/businesses/${currentBusiness.id}/settings`, {
        tax_rate: formData.tax_rate,
        late_fee_mode: formData.late_fee_mode,
        late_fee_amount: isLateFeeOff(formData.late_fee_mode) ? 0 : formData.late_fee_amount,
        currency: 'USD',
      }, {
        headers,
        withCredentials: isCookieMode,
      });

      // Refresh validation
      await refreshValidation();
      
      // Reload the page after successful completion
      window.location.reload();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to save business setup');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    const requiredFields = ['name', 'street', 'city', 'state', 'zip', 'phone'];
    const hasAllRequired = requiredFields.every(field => 
      formData[field as keyof FormData] && String(formData[field as keyof FormData]).trim()
    );
    const hasValidTaxRate = formData.tax_rate > 0;
    const hasValidLateFee = getLateFeeAmountError() === null;
    
    return hasAllRequired && hasValidTaxRate && hasValidLateFee;
  };

  const getFieldLabel = (field: string) => {
    const labels: Record<string, string> = {
      name: 'Business Name',
      street: 'Street Address',
      city: 'City',
      state: 'State',
      zip: 'ZIP Code',
      phone: 'Phone Number',
      email: 'Email Address',
      tax_rate: 'Tax Rate',
    };
    return labels[field] || field;
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        Complete Business Setup
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please complete the following required information to continue:
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {missingWarningFields.includes('email') && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Email address is recommended but not required.
            </Alert>
          )}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {/* Business Information */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Business Information
            </Typography>
            
            <TextField
              fullWidth
              label="Business Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Street Address"
              value={formData.street}
              onChange={(e) => handleInputChange('street', e.target.value)}
              required
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <TextField
                label="City"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                required
              />
              <TextField
                label="State"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                required
              />
            </Box>

            <TextField
              fullWidth
              label="ZIP Code"
              value={formData.zip}
              onChange={(e) => handleInputChange('zip', e.target.value)}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Email Address (Optional)"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              sx={{ mb: 2 }}
            />
          </Box>

          {/* Business Settings */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Business Settings
            </Typography>

            <TextField
              fullWidth
              label="Tax Rate (%)"
              type="number"
              value={formData.tax_rate}
              onChange={(e) => handleInputChange('tax_rate', parseFloat(e.target.value) || 0)}
              required
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              sx={{ mb: 3 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Late Fee Mode</InputLabel>
              <Select
                value={formData.late_fee_mode}
                onChange={(e) => handleLateFeeModeChange(e.target.value as 'flat' | 'percent' | 'off')}
                label="Late Fee Mode"
              >
                <MenuItem value="flat">Flat Fee</MenuItem>
                <MenuItem value="percent">Percentage</MenuItem>
                <MenuItem value="off">Off</MenuItem>
              </Select>
            </FormControl>

            {!isLateFeeOff(formData.late_fee_mode) && (
              <TextField
                fullWidth
                label={formData.late_fee_mode === 'percent' ? 'Late Fee Percentage (%)' : 'Late Fee Amount ($)'}
                type="number"
                value={formData.late_fee_amount}
                onChange={(e) => handleLateFeeAmountChange(parseFloat(e.target.value) || 0)}
                disabled={isLateFeeOff(formData.late_fee_mode)}
                error={!!getLateFeeAmountError()}
                helperText={getLateFeeAmountError()}
                inputProps={{
                  min: 0.01,
                  max: formData.late_fee_mode === 'percent' ? 100 : 9999.99,
                  step: 0.01,
                }}
                sx={{ mb: 2 }}
              />
            )}

            <Typography variant="body2" color="text.secondary">
              Currency: USD (Fixed)
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid() || loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : 'Save & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 